/**
 * src/lib/findingAcknowledgeOutcome.ts
 *
 * backlog #47309 — the Actions tab's "Acknowledge" button posts
 * finding-acknowledged on a §3-D gate, and what happens next is a scheduler
 * branch (adk-relay/deterministic_scheduler.py, the `elif "finding-
 * acknowledged" in tags:` branch, currently ~L7267-7310) with two materially
 * different outcomes: an investigation-only finding closes the track
 * (new_status = "complete"), anything else re-enters the dispatch pool
 * (new_status = "queued", assigned_to cleared). Nothing on the card said
 * which. This is an explicit TypeScript port of that branch (Option 2 from
 * the backlog item — the branch lives in Python and can't be shared code),
 * pinned by findingAcknowledgeOutcome.test.ts against the same signal the
 * scheduler checks, so it can't silently drift without a failing test.
 *
 * Kept in sync manually — deterministic_scheduler.py is the source of
 * truth. Same posture as ActionsTab.tsx's own SPEC_INVALID_SIGNAL_RE/
 * INVESTIGATION_SIGNAL_RE pair, which already mirrors this scheduler file
 * for a different purpose (classifying a finding *after* it's been
 * acknowledged, not before the click).
 *
 * Pure function, no Firestore/React dependency — same posture as
 * tagCorruptionWarning.ts alongside this file, testable standalone and
 * usable from ERComposerV1.tsx without pulling in any of its state.
 */

// Mirrors deterministic_scheduler.py's _INVESTIGATION_SIGNAL_RE exactly
// (same pattern, same flags) — that file's own comment notes it can't
// import this, or vice versa, since agent.py imports FROM the scheduler
// module and this is a separate TS frontend.
const INVESTIGATION_SIGNAL_RE = /no code changes( made)?|read-only investigation|investigation only,? per spec/i;

// Mirrors deterministic_scheduler.py's _CONFIDENCE_DATA_PATTERN — the
// Charter-required `confidence_data: {...}` line every finding/build-complete
// post carries.
const CONFIDENCE_DATA_RE = /confidence_data:\s*(\{[^\n]*\})/;

function extractFileCount(content: string): number | null {
  const match = CONFIDENCE_DATA_RE.exec(content);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    return parsed && typeof parsed === "object" && typeof parsed.file_count === "number"
      ? parsed.file_count
      : null;
  } catch {
    return null;
  }
}

export type FindingAcknowledgeBranch = "close" | "requeue";

// "explicit-tag" — the finding itself carries `investigation-only`, the
// structured signal the filing agent already knows for certain at
// finding-post time.
// "inferred" — no explicit tag; the regex/confidence_data fallback matched.
// An inferred "will close" that's wrong is worse than no label (AC5), so
// callers must surface this distinction rather than treating both the same.
// "default" — neither matched; the ordinary mid-build requeue case.
export type FindingAcknowledgeVia = "explicit-tag" | "inferred" | "default";

export interface FindingAcknowledgeOutcome {
  branch: FindingAcknowledgeBranch;
  via: FindingAcknowledgeVia;
}

/**
 * Predicts which branch deterministic_scheduler.py's finding-acknowledged
 * handler will take for a given finding, so the UI can show the outcome
 * before the click instead of after. `findingTags`/`findingContent` are the
 * finding (or pre-flight) message the acknowledgment gate is anchored to —
 * NOT the acknowledgment post itself.
 */
export function resolveFindingAcknowledgeOutcome(
  findingTags: string[] | undefined,
  findingContent: string | undefined,
): FindingAcknowledgeOutcome {
  const tags = findingTags ?? [];
  const content = findingContent ?? "";

  if (tags.includes("investigation-only")) {
    return { branch: "close", via: "explicit-tag" };
  }

  const fileCount = extractFileCount(content);
  if (INVESTIGATION_SIGNAL_RE.test(content) || fileCount === 0) {
    return { branch: "close", via: "inferred" };
  }

  return { branch: "requeue", via: "default" };
}
